import { useLocation, Link } from 'react-router-dom';
import { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Anchor, MapPin, Clock, ArrowRight, ChevronLeft, Navigation } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useLanguage } from '../i18n/LanguageContext';

const DESTINATIONS = {
  'location-bateau-sans-permis-punta-di-spanu': {
    name: 'Punta di Spanu',
    distance: '2 Nm',
    time: '20 min',
    image: 'https://static.wixstatic.com/media/ce6ce7_c983c0762b484e91af7f96f038bae91c~mv2.jpg',
    description: "La Punta di Spanu est l'un des joyaux de la côte de Balagne. Cette pointe rocheuse offre des eaux d'un bleu cristallin et des fonds marins exceptionnels pour le snorkeling. Accessible en seulement 20 minutes depuis le port de Sant'Ambroggio, c'est la destination idéale pour une sortie en bateau sans permis.",
    highlights: ["Eaux cristallines idéales pour le snorkeling", "Criques abritées du vent", "À seulement 20 min du port"],
    metaTitle: "Location bateau sans permis Punta di Spanu | A Bella Barca",
    metaDescription: "Découvrez Punta di Spanu en bateau sans permis depuis Sant'Ambroggio, Lumio. Eaux cristallines à 20 min du port. Réservez votre bateau chez A Bella Barca.",
  },
  'location-bateau-sans-permis-plage-aregno': {
    name: "Plage d'Aregno",
    distance: '4 Nm',
    time: '45 min',
    image: 'https://static.wixstatic.com/media/ce6ce7_96e18271a2e24df686dd439a0caea4e1~mv2.jpeg',
    description: "La plage d'Aregno, aussi connue sous le nom de plage de l'Algajola, est une magnifique étendue de sable fin bordée d'eaux turquoise. Accessible en 45 minutes en bateau sans permis depuis Sant'Ambroggio, cette plage offre un cadre paradisiaque pour une journée en mer.",
    highlights: ["Longue plage de sable fin", "Eaux turquoise peu profondes", "Mouillage facile"],
    metaTitle: "Location bateau sans permis Plage Aregno | A Bella Barca",
    metaDescription: "Rejoignez la plage d'Aregno en bateau sans permis depuis Lumio. Sable fin et eaux turquoise à 45 min. Réservez chez A Bella Barca.",
  },
  'location-bateau-sans-permis-plage-de-davia': {
    name: 'Plage de Davia',
    distance: '6 Nm',
    time: '50 min',
    image: 'https://static.wixstatic.com/media/ce6ce7_a182703a1bf646c7b2fbe11497115033~mv2.jpg',
    description: "La plage de Davia est une destination prisée pour sa beauté sauvage et ses eaux limpides. Située à environ 50 minutes en bateau sans permis depuis le port de Sant'Ambroggio, elle offre un véritable havre de paix loin de la foule estivale.",
    highlights: ["Plage sauvage et préservée", "Eaux limpides", "Cadre paisible hors des sentiers battus"],
    metaTitle: "Location bateau sans permis Plage de Davia | A Bella Barca",
    metaDescription: "Découvrez la plage de Davia en bateau sans permis depuis Sant'Ambroggio. Plage sauvage à 50 min. Réservez votre bateau chez A Bella Barca.",
  },
  'location-bateau-sans-permis-plage-de-pain-de-sucre': {
    name: 'Pain de Sucre',
    distance: '4 Nm',
    time: '40 min',
    image: 'https://static.wixstatic.com/media/ce6ce7_374c0e23ef0b41ee9457a3dc1947209f~mv2.jpg',
    description: "Le Pain de Sucre est un rocher emblématique de la côte de Balagne, entouré d'eaux d'un bleu profond. À 40 minutes en bateau sans permis depuis Sant'Ambroggio, cette formation rocheuse unique offre un spot de mouillage exceptionnel et un décor à couper le souffle.",
    highlights: ["Formation rocheuse emblématique", "Spot de mouillage exceptionnel", "Paysage à couper le souffle"],
    metaTitle: "Location bateau sans permis Pain de Sucre | A Bella Barca",
    metaDescription: "Rejoignez le Pain de Sucre en bateau sans permis depuis Lumio. Rocher emblématique et eaux profondes à 40 min. Réservez chez A Bella Barca.",
  },
  'location-bateau-sans-permis-plage-arinella': {
    name: "Plage de l'Arinella",
    distance: '3 Nm',
    time: '35 min',
    image: 'https://static.wixstatic.com/media/ce6ce7_c581debc7cd345a59768a3d998bd2035~mv2.jpg',
    description: "La plage de l'Arinella est une crique discrète et charmante, nichée entre les rochers. Accessible en 35 minutes en bateau sans permis depuis Sant'Ambroggio, elle est parfaite pour une baignade tranquille dans des eaux cristallines.",
    highlights: ["Crique discrète et charmante", "Baignade tranquille", "Accessible uniquement par la mer"],
    metaTitle: "Location bateau sans permis Plage Arinella | A Bella Barca",
    metaDescription: "Découvrez la plage de l'Arinella en bateau sans permis depuis Sant'Ambroggio. Crique discrète à 35 min. Réservez chez A Bella Barca.",
  },
  'location-bateau-sans-permis-calvi': {
    name: 'Calvi',
    distance: '5 Nm',
    time: '45 min',
    image: 'https://customer-assets-jt897jd0.emergentagent.net/job_de656f61-7fdf-428f-b9b1-eca755063a94/artifacts/c4ba5a1e18c1e26d_calvi.webp',
    description: "Calvi, avec sa citadelle majestueuse surplombant la baie, est l'une des destinations phares de la Balagne. En bateau sans permis depuis Sant'Ambroggio, rejoignez cette ville emblématique en longeant la côte et profitez d'une vue imprenable sur la citadelle depuis la mer.",
    highlights: ["Vue imprenable sur la citadelle", "Baie de Calvi turquoise", "Mouillage face à la plage"],
    metaTitle: "Location bateau sans permis Calvi | A Bella Barca",
    metaDescription: "Découvrez Calvi en bateau sans permis depuis Sant'Ambroggio, Lumio. Citadelle, baie turquoise à 45 min. Réservez votre bateau chez A Bella Barca.",
  },
  'location-bateau-sans-permis-lumio': {
    name: 'Lumio',
    distance: '1 Nm',
    time: '10 min',
    image: 'https://customer-assets-jt897jd0.emergentagent.net/job_de656f61-7fdf-428f-b9b1-eca755063a94/artifacts/c92f80facb177664_lumio.jpeg',
    description: "Lumio, village perché dominant la baie de Calvi, abrite le port de Sant'Ambroggio, point de départ de toutes nos locations. Les eaux autour de Lumio offrent des criques secrètes accessibles uniquement par la mer, idéales pour une sortie en bateau sans permis.",
    highlights: ["Port de départ Sant'Ambroggio", "Criques secrètes à proximité", "Village perché avec vue panoramique"],
    metaTitle: "Location bateau sans permis Lumio | A Bella Barca",
    metaDescription: "Location de bateau sans permis à Lumio, port de Sant'Ambroggio. Criques secrètes et eaux turquoise. Réservez chez A Bella Barca, Balagne.",
  },
  'location-bateau-sans-permis-balagne': {
    name: 'Balagne',
    distance: '',
    time: '',
    image: 'https://customer-assets-jt897jd0.emergentagent.net/job_de656f61-7fdf-428f-b9b1-eca755063a94/artifacts/89bbbf7ad3965c85_balagne.webp',
    description: "La Balagne, surnommée le « Jardin de la Corse », offre un littoral exceptionnel entre Calvi et l'Ile Rousse. Nos bateaux sans permis vous permettent d'explorer les plus belles criques de cette région : eaux turquoise, plages de sable fin et paysages sauvages vous attendent.",
    highlights: ["Le « Jardin de la Corse »", "Littoral entre Calvi et l'Ile Rousse", "Criques accessibles uniquement par la mer"],
    metaTitle: "Location bateau sans permis Balagne Corse | A Bella Barca",
    metaDescription: "Explorez la Balagne en bateau sans permis depuis Sant'Ambroggio. Les plus belles criques entre Calvi et l'Ile Rousse. Réservez chez A Bella Barca.",
  },
  'location-bateau-sans-permis-ile-rousse': {
    name: 'Ile Rousse',
    distance: '8 Nm',
    time: '1h',
    image: 'https://customer-assets-jt897jd0.emergentagent.net/job_de656f61-7fdf-428f-b9b1-eca755063a94/artifacts/3b4a620be5d47322_ile%20rousse.jpg',
    description: "L'Ile Rousse, charmante cité balnéaire de Balagne, tire son nom des rochers de porphyre rouge qui bordent son port. En bateau sans permis depuis Sant'Ambroggio, longez la côte pour découvrir cette destination unique et ses plages de sable doré.",
    highlights: ["Rochers de porphyre rouge", "Plages de sable doré", "Cité balnéaire charmante"],
    metaTitle: "Location bateau sans permis Ile Rousse | A Bella Barca",
    metaDescription: "Découvrez l'Ile Rousse en bateau sans permis depuis Lumio. Rochers rouges et plages dorées. Réservez votre bateau chez A Bella Barca, Balagne.",
  },
};

// Get all destinations for "other destinations" links
const ALL_SLUGS = Object.keys(DESTINATIONS);

export { DESTINATIONS };

export default function DestinationPage() {
  const location = useLocation();
  const slug = location.pathname.replace('/', '');
  const destination = DESTINATIONS[slug];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!destination) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-[#0a57a4] mb-2">Page introuvable</h1>
          <Link to="/" className="text-[#099dbb] underline">Retour à l'accueil</Link>
        </div>
      </div>
    );
  }

  // Update meta tags
  if (typeof document !== 'undefined') {
    document.title = destination.metaTitle;
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', destination.metaDescription);
  }

  const otherDestinations = ALL_SLUGS.filter(s => s !== slug).slice(0, 4);

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="destination-page">
      {/* Hero */}
      <div className="relative h-[50vh] min-h-[350px] overflow-hidden">
        <img src={destination.image} alt={destination.name} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
        <div className="absolute top-4 left-4 z-10">
          <Link to="/#itineraires" className="inline-flex items-center gap-1 text-white/80 hover:text-white text-sm bg-black/20 backdrop-blur-sm px-3 py-1.5 rounded-full transition-colors">
            <ChevronLeft className="w-4 h-4" /> Retour
          </Link>
        </div>
        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-12">
          <div className="max-w-5xl mx-auto">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
              <div className="inline-flex items-center gap-2 mb-3">
                <div className="w-6 h-[2px] bg-[#099dbb]" />
                <span className="text-[#099dbb] text-xs tracking-[0.2em] uppercase font-semibold">A Bella Barca</span>
              </div>
              <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-white mb-3">{destination.name}</h1>
              {(destination.distance || destination.time) && (
                <div className="flex items-center gap-4 text-white/80 text-sm">
                  {destination.distance && <span className="flex items-center gap-1"><Navigation className="w-3.5 h-3.5" /> {destination.distance} depuis Sant'Ambroggio</span>}
                  {destination.time && <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {destination.time}</span>}
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10 md:py-16">
        <div className="grid lg:grid-cols-3 gap-10">
          {/* Main text */}
          <div className="lg:col-span-2 space-y-6">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
              <p className="text-[#858275] leading-relaxed text-base">{destination.description}</p>
            </motion.div>

            {destination.highlights && (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
                <h2 className="font-heading text-xl text-[#0a57a4] mb-4">Points forts</h2>
                <ul className="space-y-2">
                  {destination.highlights.map((h, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-[#858275]">
                      <MapPin className="w-4 h-4 text-[#099dbb] shrink-0 mt-0.5" />
                      {h}
                    </li>
                  ))}
                </ul>
              </motion.div>
            )}

            {/* Placeholder for future content */}
            <div className="border-2 border-dashed border-[#dec5ae]/40 rounded-lg p-8 text-center text-[#858275]/60 text-sm">
              Photos et descriptions détaillées à venir
            </div>
          </div>

          {/* Sidebar CTA */}
          <div className="space-y-4">
            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.3 }}
              className="bg-white border border-[#dec5ae]/40 rounded-lg p-5 space-y-4 sticky top-4">
              <div className="flex items-center gap-2 mb-2">
                <Anchor className="w-5 h-5 text-[#0a57a4]" />
                <h3 className="font-heading text-lg text-[#0a57a4]">Réserver un bateau</h3>
              </div>
              <p className="text-xs text-[#858275] leading-relaxed">
                Nos bateaux sans permis (ROTO 450 S) sont parfaits pour rejoindre {destination.name}. Jusqu'à 5 passagers, entièrement équipés.
              </p>
              <Link to="/reservation">
                <Button className="w-full bg-[#099dbb] hover:bg-[#0a57a4] text-white rounded-full" data-testid="destination-book-btn">
                  Réserver maintenant <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
              <div className="pt-3 border-t border-[#dec5ae]/30 space-y-1 text-xs text-[#858275]">
                <p><MapPin className="w-3 h-3 inline mr-1 text-[#099dbb]" /> Port de Sant'Ambroggio, Lumio</p>
                <p><Clock className="w-3 h-3 inline mr-1 text-[#099dbb]" /> Demi-journée ou journée</p>
                <p><Anchor className="w-3 h-3 inline mr-1 text-[#099dbb]" /> Sans permis requis</p>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Other destinations */}
        <div className="mt-16 pt-10 border-t border-[#dec5ae]/30">
          <h2 className="font-heading text-2xl text-[#0a57a4] mb-6 text-center">Autres destinations</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {otherDestinations.map((s) => {
              const d = DESTINATIONS[s];
              return (
                <Link key={s} to={`/${s}`} className="group relative rounded-lg overflow-hidden aspect-[4/3]" data-testid={`other-dest-${s}`}>
                  <img src={d.image} alt={d.name} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                  <div className="absolute bottom-3 left-3 right-3">
                    <h3 className="font-heading text-sm text-white">{d.name}</h3>
                    {d.distance && <p className="text-white/60 text-xs">{d.distance} • {d.time}</p>}
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-[#0a57a4] text-white py-8 px-4 text-center text-sm">
        <p className="font-heading text-lg mb-2">A Bella Barca</p>
        <p className="text-white/60">Port de Sant'Ambroggio, 20260 Lumio, Corse</p>
        <p className="text-white/60">Tel : 06 87 99 96 39 | contact@abellabarca.com</p>
        <Link to="/" className="text-[#099dbb] hover:underline text-xs mt-2 inline-block">Retour à l'accueil</Link>
      </div>
    </div>
  );
}
